<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :header-height="45"
            :column-defs="columnDefs"
            :row-data="rowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <responsive-dropdown
                :icon="['fas', 'filter']"
                :items="reportOnOptions"
                variant="primary"
            >
                {{ reportLabels[reportOn] }}
            </responsive-dropdown>
            <export-to-excel-button
                :href="exportExcelHref"
            ></export-to-excel-button>
            <exportToPDFButton
                :pdfCacheKey="pdfCacheKey"
                :href="exportPdfHref"
                :pageOrientation="pageOrientation"
            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="pageOrientationOptions"
                variant="primary"
            >
                {{ pageOrientationLabel }}
            </responsive-dropdown>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
            />
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../api';
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    TOMORROW,
    THIS_WEEK,
    NEXT_WEEK,
    NEXT_MONTH,
    THIS_YEAR,
    LAST_WEEK,
    LAST_YEAR,
} from '../generic/date-range-picker/date-range-picker.vue';
import { REPORT_CHANNEL_COMMISSIONS_ENDPOINT } from '../../endpoints';
import ReportSummaryBar from './report-summary-bar.vue';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { toAmountDisplay } from '../../utils/numbers';
import { PAGE_ORIENTATION_LABELS, PAGE_ORIENTATION } from '../../constants';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
    SELECT_INPUT,
} from './report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

const REPORT_ON_INVOICE_START = 'INVOICE_START';
const REPORT_ON_INVOICE_END = 'INVOICE_END';
const reportLabels = {
    INVOICE_START: 'Invoice Start Date',
    INVOICE_END: 'Invoice End Date',
};

const exportToPDFButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    name: 'ChannelCommissionsReport',
    components: {
        Page,
        Grid,
        DateRangePicker,
        reportSummaryBar: ReportSummaryBar,
        ExportToExcelButton,
        exportToPDFButton,
        ResponsiveDropdown,
        ReportSettingsModal,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Channel Commissions',
            tabs: [
                {
                    title: 'Channel Commissions',
                    route: '/app/reports/channel-commissions/',
                    active: true,
                },
            ],
            dateRange: nameToRange(THIS_YEAR),
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                TOMORROW,
                LAST_WEEK,
                THIS_WEEK,
                NEXT_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                NEXT_MONTH,
                THIS_YEAR,
                LAST_YEAR,
            ],
            dateRequestFormat: 'yyyy-MM-dd',
            busy: false,
            gridApi: null,
            reportOn: REPORT_ON_INVOICE_START,
            reportLabels,
            rowData: [],
            totals: {},
            getRowStyle: null,
            windowWidth: 0,
            windowHeight: 0,
            statusBar: null,
            pageOrientationLabel: PAGE_ORIENTATION_LABELS[0],
            pageOrientation: PAGE_ORIENTATION[0],
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
            'isImpersonated',
        ]),
        pinnedBottomRowData() {
            return [
                {
                    rental_charges: this.totals['rental_charges'] || 0,
                    total_fee: this.totals['total_fee'] || 0,
                    channel_commission: this.totals['channel_commission'] || 0,
                },
            ];
        },
        pageOrientationOptions() {
            return [
                {
                    label: PAGE_ORIENTATION_LABELS[0],
                    click: () => {
                        this.pageOrientationLabel = PAGE_ORIENTATION_LABELS[0];
                        this.pageOrientation = PAGE_ORIENTATION[0];
                    },
                },
                {
                    label: PAGE_ORIENTATION_LABELS[1],
                    click: () => {
                        this.pageOrientationLabel = PAGE_ORIENTATION_LABELS[1];
                        this.pageOrientation = PAGE_ORIENTATION[1];
                    },
                },
            ];
        },
        columnDefs() {
            return [
                {
                    headerName: '#',
                    valueGetter: (params) => {
                        return params.node.rowPinned
                            ? this.rowData.length
                            : params.node.rowIndex + 1;
                    },
                    minWidth: 40,
                },
                {
                    field: 'created',
                    headerName: 'Created',
                    type: 'dateColumn',
                },
                {
                    field: 'partner_name',
                    headerName: 'Partner',
                    minWidth: 120,
                },
                {
                    field: 'from_date',
                    headerName: 'From Date',
                    type: 'dateColumn',
                },
                {
                    field: 'to_date',
                    headerName: 'To Date',
                    type: 'dateColumn',
                },
                {
                    headerName: 'I. Status',
                    field: 'i_status',
                },
                {
                    headerName: 'C. Status',
                    field: 'c_status',
                },
                {
                    headerName: 'Invoice #',
                    field: 'invoice_id',
                    cellRenderer: ({ node, value }) => {
                        if (!node.rowPinned && value) {
                            return `<a target="_self" href="/app/reservation/edit/invoice/${value}">${value}</a>`;
                        }
                        return '';
                    },
                },
                {
                    headerName: 'Guest Name',
                    field: 'guest_name',
                },
                {
                    field: 'property_id',
                    headerName: 'Property #',
                },
                {
                    field: 'remote_id',
                    headerName: 'Remote #',
                },
                {
                    field: 'rental_charges',
                    headerName: 'Rental Charges',
                    type: 'amountColumn',
                },
                {
                    field: 'total_fee',
                    headerName: 'Lodgix Fees',
                    type: 'amountColumn',
                },
                {
                    field: 'channel_percentage',
                    headerName: 'Channel %',
                    cellClass: 'ag-right-aligned-cell',
                    headerClass: 'ag-right-aligned-header',
                    valueGetter: ({ data, node }) => {
                        if (!node.rowPinned) {
                            return toAmountDisplay({
                                amount: data.channel_percentage,
                                percentage: true,
                            });
                        }
                        return '';
                    },
                },
                {
                    field: 'channel_commission',
                    headerName: 'Channel Commission',
                    type: 'amountColumn',
                    minWidth: 120,
                },
                {
                    field: '',
                    headerName: '',
                    autoWidthExclude: true,
                    sortable: false,
                },
            ];
        },
        reportOnOptions() {
            return [
                {
                    label: this.reportLabels[REPORT_ON_INVOICE_START],
                    click: () => {
                        this.reportOn = REPORT_ON_INVOICE_START;
                        this.fetchData();
                    },
                },
                {
                    label: this.reportLabels[REPORT_ON_INVOICE_END],
                    click: () => {
                        this.reportOn = REPORT_ON_INVOICE_END;
                        this.fetchData();
                    },
                },
            ];
        },
        exportPdfHref() {
            let url = '/app/reports/channel-commissions/row-data/pdf/';
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&report_on=${this.reportOn}`;
            return url;
        },
        exportExcelHref() {
            let url = '/app/reports/channel-commissions/row-data/excel/';
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&report_on=${this.reportOn}`;
            return url;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        isMobile() {
            const md = 768;
            return this.windowWidth < md || this.windowHeight < md;
        },
        reportSettings() {
            return {
                name: 'Channel Commissions',
                report_sysname: 'CHANNEL_COMMISSIONS',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        label: 'Date Period',
                        name: 'date',
                        predefinedRanges: this.predefinedRanges,
                    },
                    {
                        type: SELECT_INPUT,
                        value: REPORT_ON_INVOICE_START,
                        label: 'Report On',
                        name: 'report_on',
                        options: [
                            {
                                text: this.reportLabels[
                                    REPORT_ON_INVOICE_START
                                ],
                                value: REPORT_ON_INVOICE_START,
                            },
                            {
                                text: this.reportLabels[REPORT_ON_INVOICE_END],
                                value: REPORT_ON_INVOICE_END,
                            },
                        ],
                    },
                ],
            };
        },
    },
    mounted() {
        this.fetchData();
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    beforeMount() {
        this.getRowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_CHANNEL_COMMISSIONS_ENDPOINT(),
                    {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            report_on: this.reportOn,
                        },
                    }
                );
                this.rowData = data.commissions;
                this.totals = data.totals;
                this.pdfCacheKey = data.pdf_cache_key;
            } catch (e) {
                console.error(e);
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
            const statusBarComponent =
                this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance =
                    statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
};
</script>
